import React, { useState, useEffect, useContext } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { AuthContext } from '@/App';
import { Button } from '@/components/ui/button';
import { Card } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { toast } from 'sonner';
import { Save, AlertTriangle } from 'lucide-react';
import BackButton from '@/components/BackButton';

const BefuellungPage = () => {
  const { automatId } = useParams();
  const navigate = useNavigate();
  const { API } = useContext(AuthContext);
  const [automat, setAutomat] = useState(null);
  const [produkte, setProdukte] = useState([]);
  const [lagerbestaende, setLagerbestaende] = useState({});
  const [loading, setLoading] = useState(true);
  const [positions, setPositions] = useState([]);
  const [warnings, setWarnings] = useState({});

  useEffect(() => {
    fetchData();
  }, [automatId]);

  const fetchData = async () => {
    try {
      const [automatRes, produkteRes, lagerRes] = await Promise.all([
        axios.get(`${API}/automaten/${automatId}`),
        axios.get(`${API}/produkte`),
        axios.get(`${API}/lagerbestaende`),
      ]);
      setAutomat(automatRes.data);
      setProdukte(produkteRes.data.filter(p => p.active));
      
      const bestaendeMap = {};
      lagerRes.data.forEach(b => {
        bestaendeMap[b.product_id] = b.quantity;
      });
      setLagerbestaende(bestaendeMap);

      const initialPositions = [];
      for (let i = 1; i <= automatRes.data.slot_capacity; i++) {
        initialPositions.push({
          slot_number: i,
          product_id: '',
          quantity: 0,
          comment: '',
        });
      }
      setPositions(initialPositions);
    } catch (error) {
      toast.error('Fehler beim Laden der Daten');
      navigate('/');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    checkWarnings();
  }, [positions]);

  const checkWarnings = () => {
    const newWarnings = {};
    positions.forEach((pos, index) => {
      if (pos.product_id && pos.quantity > 0) {
        const produkt = produkte.find(p => p.id === pos.product_id);
        if (produkt) {
          const currentStock = lagerbestaende[pos.product_id] || 0;
          const afterStock = currentStock - pos.quantity;
          if (afterStock < produkt.min_stock) {
            newWarnings[index] = `Lagerbestand nach Befüllung: ${afterStock} (Mindestbestand: ${produkt.min_stock})`;
          }
        }
      }
    });
    setWarnings(newWarnings);
  };

  const handleSubmit = async () => {
    const validPositions = positions.filter(p => p.product_id && p.quantity > 0);
    if (validPositions.length === 0) {
      toast.error('Bitte mindestens eine Position befüllen');
      return;
    }

    try {
      await axios.post(`${API}/befuellungen`, {
        machine_id: automatId,
        type: 'nachfuellung',
        note: '',
        positions: validPositions,
      });
      toast.success('Befüllung erfolgreich gespeichert!');
      navigate(`/automaten/${automatId}`);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Fehler beim Speichern');
    }
  };

  if (loading) {
    return <div className="flex justify-center items-center h-64">Laden...</div>;
  }

  return (
    <div className="space-y-6 animate-slide-in pb-24 sm:pb-6" data-testid="befuellung-page">
      {/* Header - Desktop: Button inline, Mobile: Nur Titel */}
      <div className="flex items-center justify-between">
        <div className="flex items-center space-x-4">
          <BackButton />
          <div>
            <h1 className="text-2xl sm:text-4xl font-bold" style={{ fontFamily: 'Space Grotesk' }}>
              Automat befüllen
            </h1>
            <p className="text-slate-600 text-sm sm:text-base">{automat?.code}</p>
          </div>
        </div>
        {/* Desktop-Only Button */}
        <Button 
          onClick={handleSubmit} 
          className="hidden sm:flex items-center space-x-2" 
          data-testid="save-button-desktop"
        >
          <Save size={20} />
          <span>Speichern</span>
        </Button>
      </div>

      <div className="space-y-4">
        {positions.map((pos, index) => (
          <Card key={pos.slot_number} className="p-6" data-testid={`position-${pos.slot_number}`}>
            <h3 className="text-lg font-bold mb-4">Schacht {pos.slot_number}</h3>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <div>
                <Label>Produkt</Label>
                <Select
                  value={pos.product_id}
                  onValueChange={(value) => {
                    const newPositions = [...positions];
                    newPositions[index].product_id = value;
                    const produkt = produkte.find(p => p.id === value);
                    if (produkt) {
                      newPositions[index].quantity = produkt.default_fill_quantity;
                    }
                    setPositions(newPositions);
                  }}
                >
                  <SelectTrigger data-testid={`product-select-${pos.slot_number}`}>
                    <SelectValue placeholder="Wählen..." />
                  </SelectTrigger>
                  <SelectContent>
                    {produkte
                      .filter((produkt) => produkt.id && produkt.id.trim() !== "")
                      .map((produkt) => (
                        <SelectItem key={produkt.id} value={produkt.id}>
                          {produkt.name} (Lager: {lagerbestaende[produkt.id] || 0})
                        </SelectItem>
                      ))}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label>Menge</Label>
                <Input
                  type="number"
                  value={pos.quantity}
                  onChange={(e) => {
                    const newPositions = [...positions];
                    newPositions[index].quantity = parseInt(e.target.value) || 0;
                    setPositions(newPositions);
                  }}
                  min="0"
                  data-testid={`quantity-input-${pos.slot_number}`}
                />
              </div>
              <div>
                <Label>Kommentar (optional)</Label>
                <Input
                  value={pos.comment}
                  onChange={(e) => {
                    const newPositions = [...positions];
                    newPositions[index].comment = e.target.value;
                    setPositions(newPositions);
                  }}
                  placeholder="Bewertung"
                  data-testid={`comment-input-${pos.slot_number}`}
                />
              </div>
            </div>
            {warnings[index] && (
              <div className="mt-3 p-3 bg-amber-50 border border-amber-200 rounded-lg flex items-center space-x-2 text-amber-800 text-sm">
                <AlertTriangle size={16} />
                <span>{warnings[index]}</span>
              </div>
            )}
          </Card>
        ))}
      </div>

      {/* Mobile Sticky Action Button - Global Style */}
      <div className="mobile-action-bar sm:hidden">
        <Button 
          onClick={handleSubmit} 
          className="w-full flex items-center justify-center space-x-2 h-12" 
          data-testid="save-button-mobile"
        >
          <Save size={20} />
          <span>Speichern</span>
        </Button>
      </div>
    </div>
  );
};

export default BefuellungPage;